import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Fuse from 'fuse.js'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useSnapshot } from 'valtio'
import { store, actions, historyStore } from '@/store'
import { TaskDetailPanel } from './task-detail-panel'
import { TaskSidebar } from './sidebar'
import { MainContent } from './main-content'
import { useIsMobile } from '@/hooks/use-mobile'
import { useIsTinyScreen } from '@/hooks/use-tiny-screen'
import { useListOperations } from './hooks/use-list-operations'
import { useTodoSelection } from './hooks/use-todo-selection'
import { useTodoFiltering } from './hooks/use-todo-filtering'
import { useGlobalDragAndDrop } from './hooks/use-global-drag-and-drop'
import { useTaskDragAndDrop } from './hooks/use-task-drag-and-drop'
import { useSidebarResize } from './hooks/use-sidebar-resize'
import { showUndoToast } from '@/lib/toast-utils'
import { Trash2 } from 'lucide-react'
const uiVisibilityKeys = [
  'showTime',
  'showWeather',
  'showSidebar',
  'showStats',
  'showSearch',
  'showViewFilter',
  'showCommandCenter',
  'showShortcuts',
]
const isUiVisibilityKey = (key) => uiVisibilityKeys.includes(key)
const stopSelectoEvent = () => {
  // Selecto doesn't have a stop method, we prevent selection by not calling continue
  // The selection is stopped by returning early in the handler
}

const isPrimaryPointerEvent = (event) => {
  if (!event) return false
  if (event.pointerType === 'touch') return true
  if (typeof event.button === 'number') return event.button === 0
  return true
}

const LIST_SELECTION_REENTRY_LOCK_MS = 180

export function TodoApp() {
  const isMobile = useIsMobile()
  const isTinyScreen = useIsTinyScreen()

  return (
    <TooltipProvider>
      <TasksLayout isMobile={isMobile} isTinyScreen={isTinyScreen} />
    </TooltipProvider>
  )
}
const TasksLayout = ({ isMobile, isTinyScreen }) => {
  const [isResizing, setIsResizing] = useState(false)
  const [isDraggingList, setIsDraggingList] = useState(false)
  const listDragLockUntilRef = useRef(0)
  const snap = useSnapshot(store)
  const historySnap = useSnapshot(historyStore)
  const todos = snap.todos
  const allStatuses = useMemo(() => {
    const statuses = Array.from(new Set([...todos.map((t) => t.status), 'todo', 'done']))
    return statuses.sort((a, b) => {
      if (a === 'todo') return -1
      if (b === 'todo') return 1
      if (a === 'done') return 1
      if (b === 'done') return -1
      return a.localeCompare(b)
    })
  }, [todos])
  const [selectedListId, setSelectedListId] = useState('all')
  const lists = useMemo(() => snap.lists, [snap.lists])
  const [listSearchQuery, setListSearchQuery] = useState('')

  // Hooks
  const { handleSidebarResizeMouseDown, handleSidebarResizeKeyDown, isResizingRef } =
    useSidebarResize(snap, setIsResizing)
  const listOps = useListOperations(selectedListId, setSelectedListId)
  const selection = useTodoSelection()
  const filtering = useTodoFiltering(todos, selectedListId)
  const globalDrag = useGlobalDragAndDrop()
  const taskDrag = useTaskDragAndDrop(
    todos,
    filtering.processedTodos,
    allStatuses,
    selection.selectedIds,
  )

  // Sidebar Handlers
  const handleToggleSidebar = useCallback(() => {
    window.record('sidebar_toggled')
    actions.toggleUIVisibility('showSidebar')
  }, [])

  const handleToggleRightPanel = useCallback(() => {
    window.record('right_panel_toggled')
    actions.toggleUIVisibility('showRightPanel')
  }, [])

  const handleBackdropKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleToggleSidebar()
      }
    },
    [handleToggleSidebar],
  )

  // Lifted state for persistent sidebar logic
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('foxer-view-mode') || 'list'
    }
    return 'list'
  })

  useEffect(() => {
    localStorage.setItem('foxer-view-mode', activeTab)
  }, [activeTab])

  // List Search Logic
  const handleListSearchChange = useCallback((e) => {
    setListSearchQuery(e.target.value)
  }, [])
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }, [])
  const handleClearListSearch = useCallback(() => {
    setListSearchQuery('')
  }, [])
  const filteredLists = useMemo(() => {
    let result = lists
    if (listSearchQuery.trim()) {
      const fuse = new Fuse(lists, {
        keys: ['name'],
        threshold: 0.3,
      })
      result = fuse.search(listSearchQuery).map((res) => res.item)
    }

    // Sort by pinned status (pinned first)
    return [...result].sort((a, b) => {
      if (a.pinned === b.pinned) return 0
      return a.pinned ? -1 : 1
    })
  }, [lists, listSearchQuery])

  // Context Menu visibility
  const handleToggleVisibility = useCallback((e) => {
    const key = e.currentTarget.getAttribute('data-visibility-key')
    if (key && isUiVisibilityKey(key)) {
      actions.toggleUIVisibility(key)
    }
  }, [])
  const handleSelectAllLists = useCallback(() => {
    setSelectedListId('all')
    listOps.setSelectedListIds(new Set())
    if (isMobile) handleToggleSidebar()
  }, [isMobile, handleToggleSidebar, listOps])
  const noopListUpdate = useCallback(() => {}, [])
  const handleRenameValueChange = useCallback(
    (e) => {
      listOps.setRenameValue(e.target.value)
    },
    [listOps],
  )
  const handleRenameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        const listId = e.currentTarget.dataset.listId
        if (listId) listOps.handleRenameList(listId)
      } else if (e.key === 'Escape') {
        listOps.setRenamingListId(null)
      }
    },
    [listOps],
  )
  const handleRenameBlur = useCallback(
    (e) => {
      if (!e.relatedTarget) {
        const listId = e.currentTarget.dataset.listId
        const listName = e.currentTarget.dataset.listName
        if (
          listId &&
          listName &&
          listOps.renameValue.trim() &&
          listOps.renameValue.trim() !== listName
        ) {
          listOps.handleRenameList(listId)
        }
        listOps.setRenamingListId(null)
      }
    },
    [listOps],
  )
  const handleRenameSaveClick = useCallback(
    (e) => {
      const listId = e.currentTarget.dataset.listId
      if (listId) listOps.handleRenameList(listId)
    },
    [listOps],
  )
  const handleListItemClick = useCallback(
    (e) => {
      const listId = e.currentTarget.dataset.listId
      if (!listId) return
      const isExplicitMultiSelect = e.currentTarget.dataset.multiSelect === 'true'
      if (isExplicitMultiSelect || e.metaKey || e.ctrlKey || e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()
        listOps.setSelectedListIds((prev) => {
          const next = new Set(prev)
          if (next.has(listId)) next.delete(listId)
          else next.add(listId)
          return next
        })
        return
      }
      if (listOps.selectedListIds.size > 0) listOps.setSelectedListIds(new Set())
      setSelectedListId(listId)
      if (isMobile) handleToggleSidebar()
    },
    [isMobile, handleToggleSidebar, listOps],
  )
  const handleListItemKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      const listId = e.currentTarget.dataset.listId
      if (!listId) return
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        e.stopPropagation()
        listOps.setSelectedListIds((prev) => {
          const next = new Set(prev)
          if (next.has(listId)) next.delete(listId)
          else next.add(listId)
          return next
        })
        return
      }
      if (listOps.selectedListIds.size > 0) listOps.setSelectedListIds(new Set())
      setSelectedListId(listId)
      if (isMobile) handleToggleSidebar()
    },
    [isMobile, handleToggleSidebar, listOps],
  )
  const handleListRenameClick = useCallback(
    (e) => {
      e.stopPropagation()
      const listId = e.currentTarget.dataset.listId
      const listName = e.currentTarget.dataset.listName
      if (listId && listName) {
        listOps.setRenamingListId(listId)
        listOps.setRenameValue(listName)
      }
    },
    [listOps],
  )
  const handleListDuplicateClick = useCallback(
    (e) => {
      e.stopPropagation()
      const listId = e.currentTarget.dataset.listId
      if (listOps.selectedListIds.has(listId) && listOps.selectedListIds.size > 1) {
        listOps.bulkDuplicateLists()
        return
      }
      if (listId) listOps.handleDuplicateList(listId)
    },
    [listOps],
  )
  const handleListPinClick = useCallback(
    (e) => {
      e.stopPropagation()
      const listId = e.currentTarget.dataset.listId
      if (listId) listOps.handleToggleListPin(listId)
      e.currentTarget.blur()
    },
    [listOps],
  )
  const handleListDeleteClick = useCallback(
    (e) => {
      e.stopPropagation()
      const listId = e.currentTarget.dataset.listId
      if (listOps.selectedListIds.has(listId) && listOps.selectedListIds.size > 1) {
        listOps.bulkDeleteLists()
        return
      }
      if (!listId) return
      actions.deleteList(listId)
      window.record('list_deleted', { list_id: listId })
      if (selectedListId === listId) setSelectedListId('all')
      showUndoToast('List deleted', <Trash2 className="h-4 w-4 text-destructive" />)
    },
    [selectedListId, listOps],
  )
  const handleNewListEmojiSelect = useCallback(
    (update) => {
      if ('emoji' in update) listOps.setNewListEmoji(update.emoji)
      if ('color' in update) listOps.setNewListColor(update.color)
    },
    [listOps],
  )
  const handleNewListNameChange = useCallback(
    (e) => {
      listOps.setNewListName(e.target.value)
    },
    [listOps],
  )
  const handleNewListKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') listOps.handleAddList()
      else if (e.key === 'Escape') {
        listOps.setNewListName('')
        listOps.setNewListEmoji(undefined)
        listOps.setNewListColor(undefined)
        e.currentTarget.blur()
      }
    },
    [listOps],
  )
  const handleListDragStateChange = useCallback((isDragging) => {
    setIsDraggingList(isDragging)
    if (!isDragging) {
      listDragLockUntilRef.current = Date.now() + LIST_SELECTION_REENTRY_LOCK_MS
    }
  }, [])

  const handleListDragCondition = useCallback(
    (e) => {
      const nativeEvent = e.inputEvent
      if (!isPrimaryPointerEvent(nativeEvent)) return false

      const target = nativeEvent?.target
      if (!(target instanceof Element)) return false
      if (isDraggingList || listOps.isSelectingLists) return false
      if (Date.now() < listDragLockUntilRef.current) return false
      const isInteractive =
        target.closest('[data-list-drag-handle]') ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('[role="menuitem"]') ||
        target.closest('.group\\/list-input') ||
        target.closest('.group\\/resize') ||
        target.closest('.resize-handle') ||
        isResizingRef.current
      return !isInteractive
    },
    [isDraggingList, isResizingRef, listOps.isSelectingLists],
  )
  const handleListSelectoStart = useCallback(
    (e) => {
      const nativeEvent = e.inputEvent
      if (!isPrimaryPointerEvent(nativeEvent)) {
        stopSelectoEvent()
        return
      }
      if (Date.now() < listDragLockUntilRef.current) {
        stopSelectoEvent()
        return
      }
      if (
        isResizing ||
        isDraggingList ||
        taskDrag.isDraggingTask ||
        (selection.selectedIdsRef.current.size > 0 &&
          !nativeEvent.ctrlKey &&
          !nativeEvent.metaKey &&
          !nativeEvent.shiftKey)
      ) {
        stopSelectoEvent()
        return
      }
      listOps.setIsSelectingLists(true)
      // eslint-disable-next-line react-hooks/immutability -- Refs are mutable by design
      listOps.listSelectionSnapshotRef.current = new Set(listOps.selectedListIds)
      if (!nativeEvent.ctrlKey && !nativeEvent.metaKey && !nativeEvent.shiftKey) {
        listOps.setSelectedListIds(new Set())
      }
    },
    [isDraggingList, isResizing, taskDrag.isDraggingTask, selection.selectedIdsRef, listOps],
  )
  const handleListSelecto = useCallback(
    (e) => {
      const added = e.added.map((el) => el.getAttribute('data-list-id')).filter(Boolean)
      const removed = e.removed.map((el) => el.getAttribute('data-list-id')).filter(Boolean)
      listOps.setSelectedListIds((prev) => {
        const next = new Set(prev)
        added.forEach((id) => next.add(id))
        removed.forEach((id) => next.delete(id))
        return next
      })
    },
    [listOps],
  )
  const handleListSelectoEnd = useCallback(() => {
    listOps.suppressNextSelectionClear()
    listOps.setIsSelectingLists(false)
  }, [listOps])
  const listEmojiHandlers = useMemo(() => {
    const map = new Map()
    lists.forEach((list) => {
      map.set(list.id, (update) => actions.updateList(list.id, update))
    })
    return map
  }, [lists])

  // Lifted State from MainContent
  const [weatherUnit, setWeatherUnit] = useState('C')
  const [timeFormat, setTimeFormat] = useState('12')

  const handleToggleWeatherUnit = useCallback(() => {
    setWeatherUnit((prev) => (prev === 'C' ? 'F' : 'C'))
  }, [])

  const handleToggleTimeFormat = useCallback(() => {
    setTimeFormat((prev) => (prev === '12' ? '24' : '12'))
  }, [])
  const weatherControls = useMemo(
    () => ({
      weatherUnit,
      timeFormat,
      handleToggleWeatherUnit,
      handleToggleTimeFormat,
    }),
    [weatherUnit, timeFormat, handleToggleWeatherUnit, handleToggleTimeFormat],
  )

  const handleSidebarPeekDragEnd = useCallback((_e, info) => {
    if (info.offset.x > 50 || info.velocity.x > 500) {
      actions.setUIVisibility('showSidebar', true)
    }
  }, [])

  return (
    <div className="flex min-w-0 bg-background h-screen overflow-hidden">
      {isMobile && !snap.uiSettings.showSidebar && (
        <motion.div
          className="fixed top-16 bottom-16 left-0 z-50 w-3 bg-transparent"
          drag="x"
          dragConstraints={{
            left: 0,
            right: 0,
          }}
          dragElastic={0}
          onDragEnd={handleSidebarPeekDragEnd}
        />
      )}
      <TaskSidebar
        snap={snap}
        isMobile={isMobile}
        isResizing={isResizing}
        handleSidebarResizeMouseDown={handleSidebarResizeMouseDown}
        handleSidebarResizeKeyDown={handleSidebarResizeKeyDown}
        handleToggleSidebar={handleToggleSidebar}
        handleBackdropKeyDown={handleBackdropKeyDown}
        listSearchQuery={listSearchQuery}
        handleListSearchChange={handleListSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        handleClearListSearch={handleClearListSearch}
        handleSelectAllLists={handleSelectAllLists}
        selectedListId={selectedListId}
        todosLength={todos.length}
        filteredLists={filteredLists}
        todos={todos}
        renamingListId={listOps.renamingListId}
        listEmojiHandlers={listEmojiHandlers}
        noopListUpdate={noopListUpdate}
        renameValue={listOps.renameValue}
        handleRenameValueChange={handleRenameValueChange}
        handleRenameKeyDown={handleRenameKeyDown}
        handleRenameBlur={handleRenameBlur}
        handleRenameSaveClick={handleRenameSaveClick}
        handleListItemClick={handleListItemClick}
        handleListItemKeyDown={handleListItemKeyDown}
        selectedListIds={listOps.selectedListIds}
        handleListRenameClick={handleListRenameClick}
        handleListDuplicateClick={handleListDuplicateClick}
        handleListPinClick={handleListPinClick}
        handleListDeleteClick={handleListDeleteClick}
        newListEmoji={listOps.newListEmoji}
        newListColor={listOps.newListColor}
        handleNewListEmojiSelect={handleNewListEmojiSelect}
        newListName={listOps.newListName}
        handleNewListNameChange={handleNewListNameChange}
        handleNewListKeyDown={handleNewListKeyDown}
        handleAddList={listOps.handleAddList}
        handleListDragCondition={handleListDragCondition}
        handleListSelectoStart={handleListSelectoStart}
        handleListSelecto={handleListSelecto}
        handleListSelectoEnd={handleListSelectoEnd}
        handleToggleVisibility={handleToggleVisibility}
        weatherUnit={weatherUnit}
        timeFormat={timeFormat}
        handleToggleWeatherUnit={handleToggleWeatherUnit}
        handleToggleTimeFormat={handleToggleTimeFormat}
        setIsDraggingList={handleListDragStateChange}
      />
      <MainContent
        isMobile={isMobile}
        isTinyScreen={isTinyScreen}
        isResizing={isResizing}
        handleToggleSidebar={handleToggleSidebar}
        todos={todos}
        snap={snap}
        historySnap={historySnap}
        lists={lists}
        selectedListId={selectedListId}
        setSelectedListId={setSelectedListId}
        listOps={listOps}
        selection={selection}
        filtering={filtering}
        globalDrag={globalDrag}
        taskDrag={taskDrag}
        allStatuses={allStatuses}
        weatherUnit={weatherUnit}
        timeFormat={timeFormat}
        handleToggleWeatherUnit={handleToggleWeatherUnit}
        handleToggleTimeFormat={handleToggleTimeFormat}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <AnimatePresence>
        <TaskDetailPanel
          key="task-detail-panel"
          todo={snap.todos.find((t) => t.isExpanded)}
          isMobile={isMobile}
          handleToggleRightPanel={handleToggleRightPanel}
        />
      </AnimatePresence>
    </div>
  )
}
